import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Layout from '@/components/Layout';
import { getIdentifierLabel, getIdentifierPlaceholder, getCurrencySymbol, formatCurrency } from '@/lib/market';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Search, User, Building, TrendingUp, CreditCard, RefreshCw, ArrowUpDown, Settings, Edit, Trash2, Save, X } from 'lucide-react';
import PortfolioChart from '@/components/PortfolioChart';
import PortfolioThresholds from '@/components/PortfolioThresholds';
import NoGoList from '@/components/NoGoList';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper functions for model portfolio data
const getModelAllocation = (riskProfile, market) => {
  const isUS = market === 'US';
  const allocations = isUS ? {
    Conservative: [
      ['Investment Grade Bonds', 60],
      ['US Large Cap Equity', 15],
      ['Gold ETF', 10],
      ['Alternatives / REITs', 15]
    ],
    Balanced: [
      ['US Large Cap Equity', 40],
      ['Investment Grade Bonds', 35],
      ['International Equity', 10],
      ['Gold ETF', 5],
      ['Alternatives / PE', 10]
    ],
    Aggressive: [
      ['US Large Cap Equity', 50],
      ['Growth / Tech Equity', 15],
      ['International / EM Equity', 15],
      ['High Yield Bonds', 10],
      ['Alternatives / PE', 10]
    ]
  } : {
    Conservative: [
      ['Government Bonds', 40],
      ['Corporate Bonds', 25],
      ['Large Cap Equity', 20],
      ['Gold/Commodities', 10],
      ['Cash/Liquid Funds', 5]
    ],
    Balanced: [
      ['Large Cap Equity', 30],
      ['Mid Cap Equity', 15],
      ['Corporate Bonds', 25],
      ['Government Bonds', 15],
      ['International Equity', 10],
      ['Gold/Commodities', 5]
    ],
    Aggressive: [
      ['Large Cap Equity', 35],
      ['Mid Cap Equity', 25],
      ['Small Cap Equity', 15],
      ['International Equity', 15],
      ['Corporate Bonds', 5],
      ['Alternative Investments', 5]
    ]
  };
  return allocations[riskProfile] || allocations.Balanced;
};

const getModelSectorAllocation = (market) => market === 'US' ? [
  ['Technology', 28],
  ['Financials', 14],
  ['Healthcare', 13],
  ['Consumer Discretionary', 11],
  ['Industrials', 9],
  ['Energy', 5],
  ['REITs', 6],
  ['Others', 14]
] : [
  ['Technology', 18],
  ['Financial Services', 16],
  ['Healthcare', 12],
  ['Consumer Goods', 10],
  ['Energy', 8],
  ['Infrastructure', 8],
  ['Manufacturing', 7],
  ['Others', 21]
];

const getRecommendedEquities = (riskProfile, market) => {
  const isUS = market === 'US';
  const equities = isUS ? {
    Conservative: [
      { name: 'Apple Inc. (AAPL)', sector: 'Technology', allocation: 4, price: '189.30' },
      { name: 'Microsoft (MSFT)', sector: 'Technology', allocation: 4, price: '415.50' },
      { name: 'Johnson & Johnson (JNJ)', sector: 'Healthcare', allocation: 3, price: '152.80' },
      { name: 'JPMorgan Chase (JPM)', sector: 'Financials', allocation: 3, price: '198.40' }
    ],
    Balanced: [
      { name: 'Microsoft (MSFT)', sector: 'Technology', allocation: 5, price: '415.50' },
      { name: 'Alphabet (GOOGL)', sector: 'Technology', allocation: 4, price: '175.20' },
      { name: 'JPMorgan Chase (JPM)', sector: 'Financials', allocation: 4, price: '198.40' },
      { name: 'Amazon (AMZN)', sector: 'Consumer Discretionary', allocation: 4, price: '185.60' },
      { name: 'Berkshire Hathaway (BRK.B)', sector: 'Financials', allocation: 3, price: '367.90' }
    ],
    Aggressive: [
      { name: 'NVIDIA (NVDA)', sector: 'Technology', allocation: 6, price: '875.40' },
      { name: 'Meta Platforms (META)', sector: 'Technology', allocation: 5, price: '512.30' },
      { name: 'CrowdStrike (CRWD)', sector: 'Cybersecurity', allocation: 4, price: '324.60' },
      { name: 'Datadog (DDOG)', sector: 'Cloud Software', allocation: 4, price: '128.90' },
      { name: 'KKR & Co. (KKR)', sector: 'Private Equity', allocation: 3, price: '108.70' }
    ]
  } : {
    Conservative: [
      { name: 'Reliance Industries', sector: 'Energy', allocation: 4, price: '2,580' },
      { name: 'HDFC Bank', sector: 'Financial Services', allocation: 4, price: '1,456' },
      { name: 'TCS', sector: 'Technology', allocation: 3, price: '3,890' },
      { name: 'ITC', sector: 'Consumer Goods', allocation: 3, price: '456' }
    ],
    Balanced: [
      { name: 'Infosys', sector: 'Technology', allocation: 5, price: '1,680' },
      { name: 'ICICI Bank', sector: 'Financial Services', allocation: 4, price: '1,234' },
      { name: 'Asian Paints', sector: 'Manufacturing', allocation: 4, price: '2,890' },
      { name: 'Bajaj Finance', sector: 'Financial Services', allocation: 3, price: '6,789' },
      { name: 'Maruti Suzuki', sector: 'Automotive', allocation: 3, price: '10,456' }
    ],
    Aggressive: [
      { name: 'Zomato', sector: 'Technology', allocation: 6, price: '189' },
      { name: 'PayTM', sector: 'Fintech', allocation: 5, price: '567' },
      { name: 'PolicyBazaar', sector: 'Insurance Tech', allocation: 4, price: '1,234' },
      { name: 'Nykaa', sector: 'E-commerce', allocation: 4, price: '2,345' },
      { name: 'Adani Green Energy', sector: 'Renewable Energy', allocation: 3, price: '1,876' }
    ]
  };
  return equities[riskProfile] || equities.Balanced;
};

const getRecommendedMutualFunds = (riskProfile, market) => {
  const isUS = market === 'US';
  const funds = isUS ? {
    Conservative: [
      { name: 'Vanguard S&P 500 ETF (VOO)', category: 'Broad Market ETF', allocation: 15, nav: '487.20' },
      { name: 'iShares US Agg Bond ETF (AGG)', category: 'Investment Grade Bonds', allocation: 20, nav: '97.40' },
      { name: 'iShares 20+ Yr Treasury (TLT)', category: 'Long-Duration Treasury', allocation: 10, nav: '91.80' }
    ],
    Balanced: [
      { name: 'Vanguard Total Market ETF (VTI)', category: 'Total Market ETF', allocation: 12, nav: '245.60' },
      { name: 'Invesco QQQ ETF (QQQ)', category: 'Sector ETF — Tech', allocation: 10, nav: '476.30' },
      { name: 'iShares US Agg Bond ETF (AGG)', category: 'Investment Grade Bonds', allocation: 8, nav: '97.40' },
      { name: 'iShares MSCI EAFE ETF (EFA)', category: 'International Developed', allocation: 10, nav: '78.50' }
    ],
    Aggressive: [
      { name: 'Invesco QQQ ETF (QQQ)', category: 'Sector ETF — Tech', allocation: 15, nav: '476.30' },
      { name: 'VanEck Semiconductor ETF (SMH)', category: 'Sector ETF — Semi', allocation: 12, nav: '218.40' },
      { name: 'iShares MSCI EM ETF (VWO)', category: 'Emerging Markets ETF', allocation: 10, nav: '43.20' },
      { name: 'SPDR Gold Shares (GLD)', category: 'Gold ETF', allocation: 8, nav: '213.50' }
    ]
  } : {
    Conservative: [
      { name: 'HDFC Balanced Advantage Fund', category: 'Hybrid', allocation: 15, nav: '32.45' },
      { name: 'SBI Banking Fund', category: 'Sector', allocation: 10, nav: '156.78' },
      { name: 'ICICI Prudential Bond Fund', category: 'Debt', allocation: 25, nav: '45.67' }
    ],
    Balanced: [
      { name: 'Axis Bluechip Fund', category: 'Large Cap', allocation: 12, nav: '89.34' },
      { name: 'Mirae Asset Emerging Fund', category: 'Mid Cap', allocation: 10, nav: '67.89' },
      { name: 'HDFC Index Fund', category: 'Index', allocation: 8, nav: '234.56' },
      { name: 'Franklin India Fund', category: 'Multi Cap', allocation: 10, nav: '123.45' }
    ],
    Aggressive: [
      { name: 'SBI Small Cap Fund', category: 'Small Cap', allocation: 15, nav: '178.90' },
      { name: 'Kotak Emerging Equity', category: 'Mid Cap', allocation: 12, nav: '234.67' },
      { name: 'PGIM India Global Equity', category: 'International', allocation: 10, nav: '45.23' },
      { name: 'Quant Active Fund', category: 'Multi Cap', allocation: 8, nav: '167.89' }
    ]
  };
  return funds[riskProfile] || funds.Balanced;
};

const getInvestmentPhilosophy = (riskProfile) => {
  const philosophies = {
    Conservative: `
      <p class="mb-3">Our conservative approach prioritizes <strong>capital preservation</strong> while seeking steady income generation. This portfolio emphasizes high-quality government and corporate bonds, blue-chip equities, and defensive sectors.</p>
      <p class="mb-3"><strong>Key Principles:</strong></p>
      <ul class="list-disc pl-5 space-y-1">
        <li>Focus on capital preservation with moderate growth</li>
        <li>Emphasis on dividend-yielding, stable companies</li>
        <li>Higher allocation to fixed income instruments</li>
        <li>Lower volatility through diversified bond portfolio</li>
      </ul>
    `,
    Balanced: `
      <p class="mb-3">The balanced approach seeks to achieve <strong>optimal risk-adjusted returns</strong> through strategic diversification across asset classes. This portfolio balances growth potential with downside protection.</p>
      <p class="mb-3"><strong>Key Principles:</strong></p>
      <ul class="list-disc pl-5 space-y-1">
        <li>Balanced allocation between equity and debt</li>
        <li>Focus on quality large and mid-cap companies</li>
        <li>Geographic diversification through international exposure</li>
        <li>Regular rebalancing to maintain target allocation</li>
      </ul>
    `,
    Aggressive: `
      <p class="mb-3">The aggressive strategy targets <strong>maximum long-term capital appreciation</strong> through higher equity exposure and growth-oriented investments. This portfolio accepts higher volatility for superior returns.</p>
      <p class="mb-3"><strong>Key Principles:</strong></p>
      <ul class="list-disc pl-5 space-y-1">
        <li>High equity allocation across market caps</li>
        <li>Focus on growth companies and emerging themes</li>
        <li>International diversification for global opportunities</li>
        <li>Alternative investments for enhanced returns</li>
      </ul>
    `
  };
  return philosophies[riskProfile] || philosophies.Balanced;
};

const ClientsProspects = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('clients-prospects');
  const [clients, setClients] = useState([]);
  const [prospects, setProspects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [clientPortfolio, setClientPortfolio] = useState(null);
  const [portfolioDialogOpen, setPortfolioDialogOpen] = useState(false);
  const [riskProfileDialogOpen, setRiskProfileDialogOpen] = useState(false);
  const [clientRiskProfile, setClientRiskProfile] = useState(null);
  const [riskProfileHistory, setRiskProfileHistory] = useState([]);
  const [overrideDialogOpen, setOverrideDialogOpen] = useState(false);
  const [selectedRiskProfile, setSelectedRiskProfile] = useState('');
  const [overrideReason, setOverrideReason] = useState('');
  const [socialProfileDialogOpen, setSocialProfileDialogOpen] = useState(false);
  const [socialProfileData, setSocialProfileData] = useState(null);
  const [socialProfileForm, setSocialProfileForm] = useState({
    education: '',
    travel_preferences: '',
    sports_interests: '',
    philanthropy: '',
    lifestyle_indicators: '',
    social_media_activity: '',
    hobbies: '',
    professional_network: ''
  });
  const [modelPortfolio, setModelPortfolio] = useState(null);
  
  // Rebalancing states
  const [rebalanceDialogOpen, setRebalanceDialogOpen] = useState(false);
  const [rebalanceData, setRebalanceData] = useState(null);
  const [rebalanceLoading, setRebalanceLoading] = useState(false);
  
  // Portfolio Thresholds states
  const [thresholdsDialogOpen, setThresholdsDialogOpen] = useState(false);
  const [thresholdsClientId, setThresholdsClientId] = useState(null);
  const [thresholdsClientName, setThresholdsClientName] = useState('');
  
  // Family Management states
  const [families, setFamilies] = useState([]);
  const [selectedFamily, setSelectedFamily] = useState(null);
  const [familyOwners, setFamilyOwners] = useState([]);
  const [selectedOwner, setSelectedOwner] = useState(null);
  const [ownerAccounts, setOwnerAccounts] = useState([]);
  const [familyLoading, setFamilyLoading] = useState(false);
  
  // Restricted Asset states
  const [showAddRestrictedModal, setShowAddRestrictedModal] = useState(false);
  const [showEditRestrictedModal, setShowEditRestrictedModal] = useState(false);
  const [selectedRestrictedItem, setSelectedRestrictedItem] = useState(null);
  const [restrictedFormData, setRestrictedFormData] = useState({
    type: 'asset_class',
    name: '',
    reason: '',
    restriction_level: 'Complete Ban',
    effective_date: new Date().toISOString().split('T')[0],
    isin: '',
    review_date: ''
  });
  
  // Prospect Details states
  const [showProspectDetailsModal, setShowProspectDetailsModal] = useState(false);
  const [selectedProspect, setSelectedProspect] = useState(null);
  const [prospectPortfolio, setProspectPortfolio] = useState(null);
  const [loadingProspectDetails, setLoadingProspectDetails] = useState(false);
  
  // Document upload states
  const [prospectDocuments, setProspectDocuments] = useState([]);
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  
  // Holdings management states
  const [editingHolding, setEditingHolding] = useState(null);
  const [showAddHoldingForm, setShowAddHoldingForm] = useState(false);
  const [newHolding, setNewHolding] = useState({
    security_name: '',
    type: 'Equity',
    quantity: '',
    average_cost: '',
    current_price: '',
    asset_class: 'Equity'
  });
  
  const [formData, setFormData] = useState({
    type: 'prospect', // RM can only add prospects
    group_name: '',
    client_name: '',
    account_name: '',
    pan: '',
    mobile: '',
    email: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  // Re-fetch data when navigating back to this page (e.g. after completing questionnaire)
  useEffect(() => {
    fetchData();
  }, [location.key]);

  // Handle navigation state to set active tab
  useEffect(() => {
    if (location.state?.tab) {
      setActiveTab(location.state.tab);
    }
  }, [location]);

  const fetchData = async () => {
    try {
      // Fetch all users assigned to this RM
      const response = await axios.get(`${API}/rm/users`, { withCredentials: true });
      
      // Separate clients and prospects based on user_type
      const allUsers = response.data;
      setClients(allUsers.filter(u => u.user_type === 'client'));
      setProspects(allUsers.filter(u => u.user_type === 'prospect'));
    } catch (error) {
      console.error('Fetch data error:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to fetch data';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      // Create prospect by registering a new user
      await axios.post(`${API}/rm/add-prospect`, {
        full_name: formData.client_name,
        email: formData.email,
        mobile: formData.mobile,
        pan: formData.pan,
        group_name: formData.group_name,
        account_name: formData.account_name
      }, config);
      
      toast.success('Prospect added successfully');
      setDialogOpen(false);
      fetchData();
      setFormData({
        type: 'prospect',
        group_name: '',
        client_name: '',
        account_name: '',
        pan: '',
        mobile: '',
        email: '',
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add prospect');
    }
  };

  const viewRiskProfile = async (clientId, hasProfile) => {
    if (!hasProfile) {
      // Navigate to questionnaire for new assessment
      navigate(`/questionnaire?user_id=${clientId}`);
      return;
    }

    try {
      const creds = { withCredentials: true };

      // Fetch questionnaire/risk profile, history, and model portfolio in parallel
      const [profileRes, historyRes, modelPortfolioRes] = await Promise.all([
        axios.get(`${API}/questionnaire/user/${clientId}`, creds),
        axios.get(`${API}/questionnaire/history/${clientId}`, creds),
        axios.get(`${API}/model-portfolio/user/${clientId}`, creds).catch(() => ({ data: null }))
      ]);
      
      console.log('Profile response:', profileRes.data);
      console.log('History response:', historyRes.data);
      console.log('Model portfolio response:', modelPortfolioRes.data);
      console.log('History count:', historyRes.data?.length || 0);
      
      if (profileRes.data) {
        setClientRiskProfile(profileRes.data);
        setRiskProfileHistory(historyRes.data || []);
        setModelPortfolio(modelPortfolioRes.data);
        setSelectedClientId(clientId);
        setRiskProfileDialogOpen(true);
      } else {
        toast.error('No risk profile found. Redirecting to assessment...');
        navigate(`/questionnaire?user_id=${clientId}`);
      }
    } catch (error) {
      console.error('Risk profile fetch error:', error);
      toast.error('Failed to fetch risk profile');
      navigate(`/questionnaire?user_id=${clientId}`);
    }
  };

  const handleOverrideRiskProfile = async () => {
    if (!selectedRiskProfile) {
      toast.error('Please select a risk profile');
      return;
    }

    if (!overrideReason.trim()) {
      toast.error('Please provide a reason for override');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const payload = {
        user_id: selectedClientId,
        risk_profile: selectedRiskProfile,
        override_reason: overrideReason,
        overridden_by_rm: true,
        rm_id: user.id,
        rm_name: user.full_name
      };

      await axios.post(`${API}/questionnaire/override`, payload, config);
      
      toast.success(`Risk profile overridden to ${selectedRiskProfile}`);
      setOverrideDialogOpen(false);
      setSelectedRiskProfile('');
      setOverrideReason('');
      
      // Refresh risk profile data
      await viewRiskProfile(selectedClientId, true);
      fetchData(); // Refresh the client list
    } catch (error) {
      console.error('Override risk profile error:', error);
      toast.error(error.response?.data?.detail || 'Failed to override risk profile');
    }
  };

  const viewSocialProfile = async (clientId) => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const response = await axios.get(`${API}/social-profile/${clientId}`, config);
      
      if (response.data) {
        setSocialProfileData(response.data);
        setSocialProfileForm({
          education: response.data.education || '',
          travel_preferences: response.data.travel_preferences || '',
          sports_interests: response.data.sports_interests || '',
          philanthropy: response.data.philanthropy || '',
          lifestyle_indicators: response.data.lifestyle_indicators || '',
          social_media_activity: response.data.social_media_activity || '',
          hobbies: response.data.hobbies || '',
          professional_network: response.data.professional_network || ''
        });
      } else {
        // No profile exists, show empty form
        setSocialProfileData(null);
        setSocialProfileForm({
          education: '',
          travel_preferences: '',
          sports_interests: '',
          philanthropy: '',
          lifestyle_indicators: '',
          social_media_activity: '',
          hobbies: '',
          professional_network: ''
        });
      }
      
      setSelectedClientId(clientId);
      setSocialProfileDialogOpen(true);
    } catch (error) {
      console.error('Social profile fetch error:', error);
      if (error.response?.status === 404) {
        // Profile doesn't exist yet, show empty form
        setSocialProfileData(null);
        setSocialProfileForm({
          education: '',
          travel_preferences: '',
          sports_interests: '',
          philanthropy: '',
          lifestyle_indicators: '',
          social_media_activity: '',
          hobbies: '',
          professional_network: ''
        });
        setSelectedClientId(clientId);
        setSocialProfileDialogOpen(true);
      } else {
        toast.error('Failed to fetch social profile');
      }
    }
  };

  const saveSocialProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const payload = {
        user_id: selectedClientId,
        ...socialProfileForm,
        updated_by_rm_id: user.id,
        updated_by_rm_name: user.full_name
      };

      await axios.post(`${API}/social-profile`, payload, config);
      
      toast.success('Social profile saved successfully');
      setSocialProfileDialogOpen(false);
      fetchData(); // Refresh the client list
    } catch (error) {
      console.error('Save social profile error:', error);
      toast.error(error.response?.data?.detail || 'Failed to save social profile');
    }
  };


  const openThresholdsDialog = (clientId, clientName) => {
    setThresholdsClientId(clientId);
    setThresholdsClientName(clientName);
    setThresholdsDialogOpen(true);
  };


  // ===================== PORTFOLIO REBALANCING FUNCTIONS =====================
  
  const generateRebalancingReport = async (clientId) => {
    setRebalanceLoading(true);
    setRebalanceDialogOpen(true);
    setSelectedClientId(clientId);
    
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const response = await axios.post(`${API}/portfolio-rebalancing/${clientId}`, {}, config);
      setRebalanceData(response.data);
    } catch (error) {
      console.error('Rebalancing error:', error);
      toast.error('Failed to generate rebalancing report');
      setRebalanceDialogOpen(false);
    } finally {
      setRebalanceLoading(false);
    }
  };

  // ===================== FAMILY MANAGEMENT FUNCTIONS =====================
  
  const fetchFamilies = async () => {
    setFamilyLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/families`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFamilies(response.data.families || []);
    } catch (error) {
      console.error('Error fetching families:', error);
      toast.error('Failed to fetch families');
    } finally {
      setFamilyLoading(false);
    }
  };

  const handleFamilySelect = async (family) => {
    setSelectedFamily(family);
    setFamilyOwners([]);
    setSelectedOwner(null);
    setOwnerAccounts([]);
    setFamilyLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/owners/family/${family.family_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFamilyOwners(response.data.owners || []);
    } catch (error) {
      console.error('Error fetching owners:', error);
      toast.error('Failed to fetch family owners');
    } finally {
      setFamilyLoading(false);
    }
  };

  const handleOwnerSelect = async (owner) => {
    setSelectedOwner(owner);
    setOwnerAccounts([]);
    setFamilyLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/accounts/owner/${owner.owner_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOwnerAccounts(response.data.accounts || []);
    } catch (error) {
      console.error('Error fetching accounts:', error);
      toast.error('Failed to fetch owner accounts');
    } finally {
      setFamilyLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'family-management') {
      fetchFamilies();
    }
  }, [activeTab]);

  const viewPortfolioDetails = async (clientId) => {
    setSelectedClientId(clientId);
    setPortfolioDialogOpen(true);
    try {
      const response = await axios.get(`${API}/portfolios/client/${clientId}`, { withCredentials: true });
      if (response.data) {
        setClientPortfolio(response.data);
      } else {
        setClientPortfolio(null);
      }
    } catch (error) {
      console.log('No portfolio data yet for client:', clientId);
      setClientPortfolio(null);
    }
  };

  const convertToClient = async (prospectId) => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      await axios.post(`${API}/rm/convert-to-client/${prospectId}`, {}, config);
      toast.success('Prospect converted to client successfully');
      fetchData(); // Refresh the data
    } catch (error) {
      console.error('Convert to client error:', error);
      toast.error(error.response?.data?.detail || 'Failed to convert to client');
    }
  };

  const viewProspectDetails = async (prospectId) => {
    setLoadingProspectDetails(true);
    setShowProspectDetailsModal(true);
    
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      // Find the prospect in the prospects array
      const prospect = prospects.find(p => p.id === prospectId);
      setSelectedProspect(prospect);
      
      // Try to fetch portfolio details for the prospect (may not exist)
      try {
        const portfolioRes = await axios.get(`${API}/portfolios/user/${prospectId}`, config);
        console.log('Initial portfolio data for prospect:', portfolioRes.data);
        console.log('Holdings in initial response:', portfolioRes.data?.holdings?.length || 0);
        if (portfolioRes.data?.holdings) {
          console.log('First initial holding:', portfolioRes.data.holdings[0]);
        }
        setProspectPortfolio(portfolioRes.data);
      } catch (portfolioError) {
        console.log('No portfolio data for prospect yet:', portfolioError);
        // Portfolio doesn't exist yet - this is fine for prospects
        setProspectPortfolio(null);
      }
      
      // Fetch documents for the prospect
      try {
        const documentsRes = await axios.get(`${API}/documents/${prospectId}`, config);
        setProspectDocuments(documentsRes.data.documents || []);
      } catch (docError) {
        console.log('No documents for prospect yet:', docError);
        setProspectDocuments([]);
      }
      
    } catch (error) {
      console.error('Error fetching prospect details:', error);
      toast.error('Failed to load prospect details');
      setShowProspectDetailsModal(false);
    } finally {
      setLoadingProspectDetails(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Only images (JPEG, PNG, GIF, WebP) and PDF files are allowed');
        return;
      }
      
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File size must be less than 10MB');
        return;
      }
      
      setSelectedFile(file);
    }
  };

  const handleDocumentUpload = async () => {
    if (!selectedFile || !selectedProspect) {
      toast.error('Please select a file to upload');
      return;
    }
    
    setUploadingDocument(true);
    
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', selectedFile);
      
      const uploadResponse = await axios.post(`${API}/documents/upload/${selectedProspect.id}`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (uploadResponse.data.portfolio_extracted) {
        toast.success(`Document uploaded! Extracted ${uploadResponse.data.portfolio_summary.holdings_count} holdings from portfolio`);
        
        // Refresh portfolio data
        const config = { headers: { Authorization: `Bearer ${token}` } };
        const portfolioRes = await axios.get(`${API}/portfolios/user/${selectedProspect.id}`, config);
        console.log('Refreshed portfolio data after upload:', portfolioRes.data);
        console.log('Holdings in response:', portfolioRes.data?.holdings?.length || 0);
        if (portfolioRes.data?.holdings) {
          console.log('First holding:', portfolioRes.data.holdings[0]);
        }
        setProspectPortfolio(portfolioRes.data);
      } else {
        toast.success('Document uploaded successfully');
      }
      
      setSelectedFile(null);
      
      // Refresh documents list
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const documentsRes = await axios.get(`${API}/documents/${selectedProspect.id}`, config);
      setProspectDocuments(documentsRes.data.documents || []);
      
      // Reset file input
      const fileInput = document.getElementById('document-upload-input');
      if (fileInput) fileInput.value = '';
      
    } catch (error) {
      console.error('Error uploading document:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploadingDocument(false);
    }
  };

  const deleteDocument = async (documentId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      await axios.delete(`${API}/documents/${documentId}`, config);
      toast.success('Document deleted successfully');
      
      // Refresh documents list
      setProspectDocuments(prospectDocuments.filter(doc => doc.id !== documentId));
      
    } catch (error) {
      console.error('Error deleting document:', error);
      toast.error('Failed to delete document');
    }
  };

  const downloadDocument = async (documentId) => {
    try {
      const response = await axios.get(`${API}/documents/download/${documentId}`);
      window.open(response.data.download_url, '_blank');
    } catch (error) {
      console.error('Download failed:', error);
      toast.error('Failed to download document');
    }
  };

  const filterData = (data) => {
    if (!searchTerm) return data;
    return data.filter(
      (item) =>
        item.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (item.pan && item.pan.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.group_name && item.group_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.email && item.email.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  };

  const DataTable = ({ data, type }) => (
    <div className="space-y-4">
      {data.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <User className="w-16 h-16 mx-auto mb-4 opacity-50" />
          <p>No {type}s found</p>
        </div>
      ) : (
        data.map((item) => (
          <Card key={item.id} className="border-gray-200 bg-white shadow-sm hover:shadow-md transition-shadow" data-testid={`${type}-card-${item.id}`}>
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div className="space-y-2">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-[#fffdf0] flex items-center justify-center">
                      {item.group_name ? <Building className="w-6 h-6 text-ugmonk-dark" /> : <User className="w-6 h-6 text-ugmonk-dark" />}
                    </div>
                    <div>
                      <h3 className="text-gray-900 font-semibold text-lg">{item.full_name}</h3>
                      {item.group_name && <p className="text-gray-600 text-sm">{item.group_name}</p>}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4 text-sm">
                    {(item.pan || item.ssn) && (
                      <div>
                        <span className="text-gray-500">{getIdentifierLabel(user?.market)}:</span>
                        <p className="text-gray-900">{item.ssn || item.pan}</p>
                      </div>
                    )}
                    {item.mobile && (
                      <div>
                        <span className="text-gray-500">Mobile:</span>
                        <p className="text-gray-900">{item.mobile}</p>
                      </div>
                    )}
                    {item.email && (
                      <div>
                        <span className="text-gray-500">Email:</span>
                        <p className="text-gray-900">{item.email}</p>
                      </div>
                    )}
                    {item.account_number && (
                      <div>
                        <span className="text-gray-500">Account:</span>
                        <p className="text-gray-900">{item.account_number}</p>
                      </div>
                    )}
                    {item.risk_profile && (
                      <div>
                        <span className="text-gray-500">Risk Profile:</span>
                        <span className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${
                          item.risk_profile === 'Conservative' ? 'bg-[#fffdf0] text-ugmonk-dark' :
                          item.risk_profile === 'Balanced' ? 'bg-yellow-100 text-yellow-700' :
                          item.risk_profile === 'Not Assessed' ? 'bg-gray-100 text-gray-600' :
                          'bg-[#fffdf0] text-ugmonk-dark'
                        }`}>
                          {item.risk_profile}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  {type === 'client' ? (
                    <>
                      <Button 
                        size="sm" 
                        className="bg-ugmonk-yellow hover:bg-[#f5cc6a] text-black" 
                        onClick={() => viewRiskProfile(item.id, item.questionnaire_completed)}
                      >
                        <TrendingUp size={14} className="mr-1" />
                        {item.questionnaire_completed ? 'View Risk Profile' : 'Assess Risk Profile'}
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="border-[#1a1a1a] text-ugmonk-dark hover:bg-[#fffdf0] hover:text-ugmonk-dark"
                        onClick={() => viewPortfolioDetails(item.id)}
                      >
                        View Details
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="border-purple-600 text-purple-600 hover:bg-purple-50 hover:text-purple-700"
                        onClick={() => generateRebalancingReport(item.id)}
                      >
                        <RefreshCw size={14} className="mr-1" />
                        Portfolio Rebalancing
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="border-[#ffd87a] text-ugmonk-dark hover:bg-[#fffdf0] hover:text-ugmonk-dark"
                        onClick={() => viewSocialProfile(item.id)}
                      >
                        Social Profile
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="border-gray-600 text-gray-600 hover:bg-gray-50"
                        onClick={() => openThresholdsDialog(item.id, item.full_name)}
                      >
                        <Settings size={14} className="mr-1" />
                        Thresholds
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="border-[#1a1a1a] text-ugmonk-dark hover:bg-[#fffdf0] hover:text-ugmonk-dark"
                        onClick={() => viewProspectDetails(item.id)}
                      >
                        View Details
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="border-[#ffd87a] text-ugmonk-dark hover:bg-[#fffdf0] hover:text-ugmonk-dark"
                        onClick={() => viewSocialProfile(item.id)}
                      >
                        Social Profile
                      </Button>
                      <Button 
                        size="sm" 
                        className="bg-[#1a1a1a] hover:bg-[#1a1a1a] text-white"
                        onClick={() => convertToClient(item.id)}
                      >
                        Convert to Client
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6" data-testid="clients-prospects-container">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2" style={{fontFamily: "'PerfectNineties', serif"}}>
              Clients & Prospects
            </h1>
            <p className="text-gray-600">Manage your client database and family hierarchy</p>
          </div>
          {activeTab === 'clients-prospects' && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-ugmonk-yellow hover:bg-[#f5cc6a] text-black" data-testid="add-client-button">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Prospect
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-2xl">
              <DialogHeader>
                <DialogTitle className="text-gray-900">Add Prospect</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="add-client-form">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="client_name" className="text-gray-700">Prospect Name *</Label>
                    <Input
                      id="client_name"
                      value={formData.client_name}
                      onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                      required
                      className="bg-white border-gray-300"
                      data-testid="client-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="group_name" className="text-gray-700">Group Name</Label>
                    <Input
                      id="group_name"
                      value={formData.group_name}
                      onChange={(e) => setFormData({ ...formData, group_name: e.target.value })}
                      className="bg-white border-gray-300"
                      data-testid="group-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="account_name" className="text-gray-700">Account Name</Label>
                    <Input
                      id="account_name"
                      value={formData.account_name}
                      onChange={(e) => setFormData({ ...formData, account_name: e.target.value })}
                      className="bg-white border-gray-300"
                      data-testid="account-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="pan" className="text-gray-700">{getIdentifierLabel(user?.market)} *</Label>
                    <Input
                      id="pan"
                      value={formData.pan}
                      onChange={(e) => setFormData({ ...formData, pan: e.target.value.toUpperCase() })}
                      required
                      placeholder={getIdentifierPlaceholder(user?.market)}
                      className="bg-white border-gray-300"
                      data-testid="pan-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="mobile" className="text-gray-700">Mobile *</Label>
                    <Input
                      id="mobile"
                      value={formData.mobile}
                      onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                      required
                      className="bg-white border-gray-300"
                      data-testid="mobile-input"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label htmlFor="email" className="text-gray-700">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="bg-white border-gray-300"
                      data-testid="email-input"
                    />
                  </div>
                </div>
                <Button type="submit" className="w-full bg-ugmonk-yellow hover:bg-[#f5cc6a] text-black" data-testid="submit-client-button">
                  Add Prospect
                </Button>
              </form>
            </DialogContent>
          </Dialog>
          )}
        </div>

        {activeTab === 'clients-prospects' && (
          <Card className="border-gray-200 bg-white shadow-sm">
          <CardHeader className="py-3">
            <div className="flex flex-col md:flex-row gap-3 items-start md:items-center justify-between">
              <CardTitle className="text-gray-900">Search</CardTitle>
              <div className="relative w-full md:w-96">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
                <Input
                  placeholder="Search by name, PAN, or group..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-white border-gray-300 text-gray-900 h-9 text-sm"
                  data-testid="search-input"
                />
              </div>
            </div>
          </CardHeader>
        </Card>
        )}

        {/* Main Tabs for Clients/Prospects and Family Management */}
        <div className="border-b border-gray-200">
          <div className="flex gap-2">
            <button
              onClick={() => setActiveTab('clients-prospects')}
              className={`px-6 py-3 font-medium text-sm border-b-2 transition-colors ${
                activeTab === 'clients-prospects'
                  ? 'border-[#ffd87a] text-ugmonk-dark'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              <User className="w-4 h-4 inline mr-2" />
              Clients & Prospects
            </button>
            <button
              onClick={() => setActiveTab('family-management')}
              className={`px-6 py-3 font-medium text-sm border-b-2 transition-colors ${
                activeTab === 'family-management'
                  ? 'border-[#ffd87a] text-ugmonk-dark'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              <Building className="w-4 h-4 inline mr-2" />
              Family Management
            </button>
          </div>
        </div>

        {/* Clients & Prospects Tab Content */}
        {activeTab === 'clients-prospects' && (
          <>
            <Tabs defaultValue="clients" className="w-full">
          <TabsList className="grid w-full grid-cols-2 max-w-md">
            <TabsTrigger value="clients" data-testid="clients-tab">
              Clients ({clients.length})
            </TabsTrigger>
            <TabsTrigger value="prospects" data-testid="prospects-tab">
              Prospects ({prospects.length})
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="clients" className="mt-6">
            {loading ? (
              <div className="space-y-4">
                {[...Array(3)].map((_, i) => (
                  <Card key={i} className="border-gray-200 bg-white">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-3">
                        <div className="w-12 h-12 rounded-full bg-gray-200 animate-pulse" />
                        <div className="flex-1 space-y-3">
                          <div className="h-5 w-40 bg-gray-200 rounded animate-pulse" />
                          <div className="grid grid-cols-3 gap-4">
                            <div className="h-4 bg-gray-100 rounded animate-pulse" />
                            <div className="h-4 bg-gray-100 rounded animate-pulse" />
                            <div className="h-4 bg-gray-100 rounded animate-pulse" />
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <DataTable data={filterData(clients)} type="client" />
            )}
          </TabsContent>

          <TabsContent value="prospects" className="mt-6">
            {loading ? (
              <div className="space-y-4">
                {[...Array(3)].map((_, i) => (
                  <Card key={i} className="border-gray-200 bg-white">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-3">
                        <div className="w-12 h-12 rounded-full bg-gray-200 animate-pulse" />
                        <div className="flex-1 space-y-3">
                          <div className="h-5 w-40 bg-gray-200 rounded animate-pulse" />
                          <div className="grid grid-cols-3 gap-4">
                            <div className="h-4 bg-gray-100 rounded animate-pulse" />
                            <div className="h-4 bg-gray-100 rounded animate-pulse" />
                            <div className="h-4 bg-gray-100 rounded animate-pulse" />
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <DataTable data={filterData(prospects)} type="prospect" />
            )}
          </TabsContent>
        </Tabs>
          </>
        )}

        {/* Family Management Tab Content */}
        {activeTab === 'family-management' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Families List */}
            <Card className="border-gray-200 bg-white">
              <CardHeader className="pb-3">
                <CardTitle className="text-gray-900 flex items-center gap-2">
                  <Building className="w-5 h-5 text-ugmonk-dark" />
                  Families
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 max-h-96 overflow-y-auto">
                {familyLoading && !families.length ? (
                  <div className="space-y-2">
                    {[...Array(3)].map((_, i) => (
                      <div key={i} className="p-4 rounded-lg border border-gray-200">
                        <div className="h-4 w-32 bg-gray-200 rounded animate-pulse mb-2" />
                        <div className="h-3 w-48 bg-gray-100 rounded animate-pulse" />
                      </div>
                    ))}
                  </div>
                ) : families.length === 0 ? (
                  <p className="text-center py-8 text-gray-500">No families found</p>
                ) : (
                  <div className="space-y-2">
                    {families.map((family) => (
                      <div
                        key={family.family_id}
                        onClick={() => handleFamilySelect(family)}
                        className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                          selectedFamily?.family_id === family.family_id
                            ? 'border-[#ffd87a] bg-[#ffd87a]/5'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="font-semibold text-gray-900">{family.family_name}</div>
                        <div className="text-sm text-gray-600">
                          {family.total_owners} owners · {formatCurrency(family.total_aum, user?.market)} AUM
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Owners List */}
            <Card className="border-gray-200 bg-white">
              <CardHeader className="pb-3">
                <CardTitle className="text-gray-900 flex items-center gap-2">
                  <User className="w-5 h-5 text-ugmonk-dark" />
                  Owners
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 max-h-96 overflow-y-auto">
                {!selectedFamily ? (
                  <div className="text-center py-8 text-gray-500">
                    <User className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>Select a family to view owners</p>
                  </div>
                ) : familyLoading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1a1a1a] mx-auto mb-3"></div>
                    <p className="text-gray-500">Loading owners...</p>
                  </div>
                ) : familyOwners.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <User className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>No owners found in {selectedFamily?.family_name}</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {familyOwners.map((owner) => (
                      <div
                        key={owner.owner_id}
                        onClick={() => handleOwnerSelect(owner)}
                        className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                          selectedOwner?.owner_id === owner.owner_id
                            ? 'border-[#ffd87a] bg-[#ffd87a]/5'
                            : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                        }`}
                      >
                        <div className="font-semibold text-gray-900 text-sm">{owner.owner_name}</div>
                        <div className="text-xs text-gray-600">PAN: {owner.pan || 'N/A'}</div>
                        <div className="text-xs text-gray-600">Mobile: {owner.mobile || 'N/A'}</div>
                        <div className="text-xs text-ugmonk-dark">
                          AUM: {formatCurrency(owner.total_aum || 0, user?.market)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Accounts List */}
            <Card className="border-gray-200 bg-white">
              <CardHeader className="pb-3">
                <CardTitle className="text-gray-900 flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-ugmonk-dark" />
                  Accounts ({ownerAccounts.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 max-h-96 overflow-y-auto">
                {!selectedOwner ? (
                  <div className="text-center py-8 text-gray-500">
                    <CreditCard className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>Select an owner to view accounts</p>
                  </div>
                ) : familyLoading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#ffd87a] mx-auto mb-3"></div>
                    <p className="text-gray-500">Loading accounts...</p>
                  </div>
                ) : ownerAccounts.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <CreditCard className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>No accounts found for this owner</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {ownerAccounts.map((account) => (
                      <div
                        key={account.account_id}
                        className="p-3 rounded-lg border bg-gray-50 border-gray-200 hover:bg-gray-100 transition-colors"
                      >
                        <div className="font-semibold text-gray-900 text-sm">{account.account_name}</div>
                        <div className="text-xs text-gray-600">Type: {account.account_type || 'Investment'}</div>
                        <div className="text-xs text-gray-600">Number: {account.account_number}</div>
                        {account.is_held_away && (
                          <div className="text-xs text-ugmonk-dark font-medium">⚠️ Held Away Account</div>
                        )}
                        <div className="text-xs text-ugmonk-dark font-semibold">
                          Value: {formatCurrency(account.aum || account.total_value || 0, user?.market)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Portfolio Details Dialog */}
        <Dialog open={portfolioDialogOpen} onOpenChange={setPortfolioDialogOpen}>
          <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-5xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-gray-900 text-2xl font-bold">Portfolio Details</DialogTitle>
            </DialogHeader>
            {clientPortfolio ? (
              <div className="space-y-6">
                {/* Summary Cards */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="p-5 bg-gradient-to-br from-[#fffdf0] to-[#fffdf0] rounded-xl border border-ugmonk-border">
                    <p className="text-gray-600 text-sm mb-1">Total Market Value</p>
                    <p className="text-2xl font-bold text-gray-900">{formatCurrency(clientPortfolio.total_market_value || 0, user?.market)}</p>
                  </div>
                  <div className="p-5 bg-gradient-to-br from-[#fffdf0] to-[#fffdf0] rounded-xl border border-ugmonk-border">
                    <p className="text-gray-600 text-sm mb-1">Total Invested</p>
                    <p className="text-2xl font-bold text-gray-900">{formatCurrency((clientPortfolio.total_market_value || 0) * 0.85, user?.market)}</p>
                  </div>
                  <div className="p-5 bg-gradient-to-br from-green-50 to-green-100 rounded-xl border border-green-200">
                    <p className="text-gray-600 text-sm mb-1">Unrealized Gain</p>
                    <p className="text-2xl font-bold text-green-600">{formatCurrency(clientPortfolio.total_unrealized_gain || 0, user?.market)}</p>
                  </div>
                </div>

                {/* Holdings by Asset Class */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900">Holdings by Asset Class</h3>
                  
                  {/* Group holdings by asset class */}
                  {clientPortfolio.holdings && Object.entries(
                    clientPortfolio.holdings.reduce((acc, holding) => {
                      const assetClass = holding.asset_class || 'Other';
                      if (!acc[assetClass]) acc[assetClass] = [];
                      acc[assetClass].push(holding);
                      return acc;
                    }, {})
                  ).map(([assetClass, holdings]) => (
                    <div key={assetClass} className="bg-gray-50 rounded-lg border border-gray-200 p-4">
                      <h4 className="font-semibold text-gray-900 mb-3 flex items-center justify-between">
                        <span>{assetClass}</span>
                        <span className="text-sm text-gray-600">
                          {holdings.length} {holdings.length === 1 ? 'holding' : 'holdings'}
                        </span>
                      </h4>
                      <div className="space-y-2">
                        {holdings.map((holding, idx) => (
                          <div key={idx} className="bg-white rounded-lg p-3 border border-gray-200">
                            <div className="flex justify-between items-start mb-2">
                              <div className="flex-1">
                                <p className="font-medium text-gray-900">{holding.security_name || holding.scheme_name}</p>
                                {holding.isin && <p className="text-xs text-gray-500">ISIN: {holding.isin}</p>}
                              </div>
                              <div className="text-right">
                                <p className="font-semibold text-gray-900">{formatCurrency(holding.market_value || 0, user?.market)}</p>
                                <p className={`text-xs font-medium ${(holding.unrealized_gain || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                  {(holding.unrealized_gain || 0) >= 0 ? '+' : ''}{formatCurrency(holding.unrealized_gain || 0, user?.market)}
                                </p>
                              </div>
                            </div>
                            <div className="grid grid-cols-3 gap-2 text-xs text-gray-600">
                              <div>
                                <span className="text-gray-500">Qty:</span> {holding.quantity || 0}
                              </div>
                              <div>
                                <span className="text-gray-500">Avg Cost:</span> {getCurrencySymbol(user?.market)}{(holding.average_cost || 0).toFixed(2)}
                              </div>
                              <div>
                                <span className="text-gray-500">Current:</span> {getCurrencySymbol(user?.market)}{(holding.current_price || 0).toFixed(2)}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Chart */}
                {clientPortfolio.holdings && clientPortfolio.holdings.length > 0 && (
                  <div className="bg-white rounded-lg border border-gray-200 p-5">
                    <h4 className="font-semibold text-gray-900 mb-3">Asset Allocation</h4>
                    <PortfolioChart portfolio={clientPortfolio} />
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-600">No portfolio data available</p>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Risk Profile Dialog */}
        <Dialog open={riskProfileDialogOpen} onOpenChange={setRiskProfileDialogOpen}>
          <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-6xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-gray-900 text-2xl font-bold">Risk Profile & Investment Preferences</DialogTitle>
            </DialogHeader>
            
            <Tabs defaultValue="risk-profile" className="space-y-4">
              <TabsList className="bg-gray-100">
                <TabsTrigger value="risk-profile" className="data-[state=active]:bg-white">
                  Risk Profile
                </TabsTrigger>
                <TabsTrigger value="model-portfolio" className="data-[state=active]:bg-white">
                  House View
                </TabsTrigger>
                <TabsTrigger value="no-go-list" className="data-[state=active]:bg-white">
                  No Go List
                </TabsTrigger>
              </TabsList>

              <TabsContent value="risk-profile">
            {clientRiskProfile ? (
              <div className="space-y-6">
                {/* Current Risk Profile Header */}
                <div className="bg-gradient-to-br from-[#fffdf0] to-[#fffdf0] rounded-xl p-6 border border-ugmonk-border">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">Current Risk Profile</h3>
                      <p className="text-3xl font-bold text-ugmonk-dark">{clientRiskProfile.risk_profile_determined || 'Not Assessed'}</p>
                      <p className="text-sm text-gray-600 mt-1">
                        Last Updated: {new Date(clientRiskProfile.last_modified || clientRiskProfile.created_at).toLocaleDateString('en-IN', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric'
                        })}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-600 mb-1">Risk Score</p>
                      <p className="text-4xl font-bold text-ugmonk-dark">{clientRiskProfile.risk_score || 0}</p>
                      <p className="text-xs text-gray-500 mt-1">out of 100</p>
                    </div>
                  </div>
                </div>

                {/* Asset Allocation */}
                {clientRiskProfile.recommended_allocation && (
                  <div className="bg-white rounded-lg border border-gray-200 p-5">
                    <h4 className="font-semibold text-gray-900 mb-3">Recommended Asset Allocation</h4>
                    <div className="grid grid-cols-2 gap-3">
                      {Object.entries(clientRiskProfile.recommended_allocation).map(([key, value]) => (
                        <div key={key} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                          <span className="text-sm text-gray-700 capitalize">{key.replace('_', ' ')}</span>
                          <span className="text-sm font-semibold text-gray-900">{value}%</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Assessment Summary */}
                <div className="bg-white rounded-lg border border-gray-200 p-5">
                  <h4 className="font-semibold text-gray-900 mb-3">Assessment Summary</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Investment Horizon:</span>
                      <span className="font-medium text-gray-900">{clientRiskProfile.investment_horizon || 'Not specified'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Investment Experience:</span>
                      <span className="font-medium text-gray-900 capitalize">{clientRiskProfile.investment_experience || 'Not specified'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Assessment Date:</span>
                      <span className="font-medium text-gray-900">
                        {new Date(clientRiskProfile.created_at || clientRiskProfile.completed_at).toLocaleDateString('en-IN')}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Risk Profile History */}
                {riskProfileHistory && riskProfileHistory.length > 0 && (
                  <div className="bg-white rounded-lg border border-gray-200 p-5">
                    <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <svg className="w-5 h-5 text-ugmonk-dark" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Assessment History ({riskProfileHistory.length})
                    </h4>
                    <div className="space-y-3 max-h-64 overflow-y-auto">
                      {riskProfileHistory.map((entry, idx) => (
                        <div key={entry.id || idx} className={`p-4 rounded-lg border ${
                          idx === 0 ? 'bg-[#fffdf0] border-ugmonk-border' : 'bg-gray-50 border-gray-200'
                        }`}>
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                                  entry.risk_profile === 'Conservative' ? 'bg-[#fffdf0] text-ugmonk-dark' :
                                  entry.risk_profile === 'Balanced' ? 'bg-yellow-100 text-yellow-700' :
                                  'bg-red-100 text-red-700'
                                }`}>
                                  {entry.risk_profile}
                                </span>
                                <span className="text-lg font-bold text-gray-900">{entry.risk_score}/100</span>
                                {idx === 0 && (
                                  <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                                    Current
                                  </span>
                                )}
                              </div>
                              <div className="text-xs text-gray-600 space-y-1">
                                <p>Assessed: {new Date(entry.assessed_at).toLocaleDateString('en-IN', {
                                  day: 'numeric',
                                  month: 'short',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}</p>
                                <p>By: {entry.assessed_by}</p>
                              </div>
                            </div>
                            <div className="text-right text-xs text-gray-600">
                              {entry.investment_horizon && (
                                <div className="mb-1">
                                  <span className="font-medium">Horizon:</span> {entry.investment_horizon}
                                </div>
                              )}
                              {entry.investment_experience && (
                                <div>
                                  <span className="font-medium">Experience:</span> {entry.investment_experience}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex gap-3 pt-4 border-t">
                  <Button 
                    onClick={() => {
                      setRiskProfileDialogOpen(false);
                      navigate(`/questionnaire?user_id=${selectedClientId}&retake=true`);
                    }}
                    className="flex-1 bg-ugmonk-yellow hover:bg-[#f5cc6a] text-black"
                  >
                    Retake Questionnaire
                  </Button>
                  <Button 
                    onClick={() => {
                      setRiskProfileDialogOpen(false);
                      setOverrideDialogOpen(true);
                    }}
                    className="flex-1 bg-[#1a1a1a] hover:bg-[#1a1a1a] text-white"
                  >
                    Override Questionnaire
                  </Button>
                  <Button 
                    onClick={() => setRiskProfileDialogOpen(false)}
                    variant="outline"
                    className="border-gray-300 text-gray-700 hover:bg-gray-50"
                  >
                    Close
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-600 mb-4">No risk profile found for this client</p>
                <Button 
                  onClick={() => {
                    setRiskProfileDialogOpen(false);
                    navigate(`/questionnaire?user_id=${selectedClientId}`);
                  }}
                  className="bg-ugmonk-yellow hover:bg-[#f5cc6a] text-black"
                >
                  Start Questionnaire
                </Button>
              </div>
            )}
              </TabsContent>

              <TabsContent value="model-portfolio">
                {clientRiskProfile && (
                  <div className="space-y-6">
                    {/* House View Header */}
                    <div className="bg-gradient-to-br from-[#fffdf0] to-[#f0fdf4] rounded-xl p-6 border border-ugmonk-border">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900 mb-2">
                            House View - {clientRiskProfile.risk_profile_determined || 'Not Assessed'} Portfolio
                          </h3>
                          <p className="text-sm text-gray-600">
                            Sanctum Wealth's recommended model portfolio for {clientRiskProfile.risk_profile_determined?.toLowerCase()} risk investors
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-gray-600 mb-1">Target Return</p>
                          <p className="text-3xl font-bold text-ugmonk-dark">
                            {clientRiskProfile.risk_profile_determined === 'Conservative' ? '8-10%' :
                             clientRiskProfile.risk_profile_determined === 'Balanced' ? '10-12%' :
                             clientRiskProfile.risk_profile_determined === 'Aggressive' ? '12-15%' : '8-12%'}
                          </p>
                          <p className="text-xs text-gray-500">per annum</p>
                        </div>
                      </div>
                    </div>

                    {/* Asset Allocation */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <div className="bg-white rounded-lg border border-gray-200 p-5">
                        <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                          <div className="w-4 h-4 bg-ugmonk-yellow rounded"></div>
                          Strategic Asset Allocation
                        </h4>
                        <div className="space-y-3">
                          {getModelAllocation(clientRiskProfile.risk_profile_determined, user?.market).map(([asset, percentage]) => (
                            <div key={asset} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                              <span className="text-sm text-gray-700 font-medium">{asset}</span>
                              <div className="flex items-center gap-2">
                                <div className="w-16 bg-gray-200 rounded-full h-2">
                                  <div 
                                    className="bg-ugmonk-yellow h-2 rounded-full" 
                                    style={{ width: `${percentage}%` }}
                                  ></div>
                                </div>
                                <span className="text-sm font-semibold text-gray-900 w-10">{percentage}%</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="bg-white rounded-lg border border-gray-200 p-5">
                        <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                          <div className="w-4 h-4 bg-green-500 rounded"></div>
                          Sector Allocation (Equity Portion)
                        </h4>
                        <div className="space-y-3">
                          {getModelSectorAllocation(user?.market).map(([sector, percentage]) => (
                            <div key={sector} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                              <span className="text-sm text-gray-700 font-medium">{sector}</span>
                              <div className="flex items-center gap-2">
                                <div className="w-16 bg-gray-200 rounded-full h-2">
                                  <div 
                                    className="bg-green-500 h-2 rounded-full" 
                                    style={{ width: `${percentage * 2}%` }}
                                  ></div>
                                </div>
                                <span className="text-sm font-semibold text-gray-900 w-10">{percentage}%</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Recommended Securities */}
                    <div className="bg-white rounded-lg border border-gray-200 p-5">
                      <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <div className="w-4 h-4 bg-ugmonk-yellow rounded"></div>
                        Recommended Securities
                      </h4>
                      
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Direct Equity */}
                        <div>
                          <h5 className="font-medium text-gray-800 mb-3 flex items-center gap-2">
                            <TrendingUp className="h-4 w-4 text-ugmonk-dark" />
                            Direct Equity
                          </h5>
                          <div className="space-y-2">
                            {getRecommendedEquities(clientRiskProfile.risk_profile_determined, user?.market).map((stock, index) => (
                              <div key={index} className="flex justify-between items-center p-2 bg-[#fffdf0] rounded">
                                <div>
                                  <span className="text-sm font-medium text-gray-900">{stock.name}</span>
                                  <div className="text-xs text-gray-600">{stock.sector}</div>
                                </div>
                                <div className="text-right">
                                  <div className="text-sm font-medium text-ugmonk-dark">{stock.allocation}%</div>
                                  <div className="text-xs text-gray-500">{user?.market === 'US' ? '$' : '₹'}{stock.price}</div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Mutual Funds / ETFs */}
                        <div>
                          <h5 className="font-medium text-gray-800 mb-3 flex items-center gap-2">
                            <Building className="h-4 w-4 text-green-600" />
                            {user?.market === 'US' ? 'ETFs & Funds' : 'Mutual Funds'}
                          </h5>
                          <div className="space-y-2">
                            {getRecommendedMutualFunds(clientRiskProfile.risk_profile_determined, user?.market).map((fund, index) => (
                              <div key={index} className="flex justify-between items-center p-2 bg-green-50 rounded">
                                <div>
                                  <span className="text-sm font-medium text-gray-900">{fund.name}</span>
                                  <div className="text-xs text-gray-600">{fund.category}</div>
                                </div>
                                <div className="text-right">
                                  <div className="text-sm font-medium text-green-600">{fund.allocation}%</div>
                                  <div className="text-xs text-gray-500">{user?.market === 'US' ? 'Price: $' : 'NAV: ₹'}{fund.nav}</div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Investment Philosophy */}
                    <div className="bg-white rounded-lg border border-gray-200 p-5">
                      <h4 className="font-semibold text-gray-900 mb-3">Investment Philosophy & Strategy</h4>
                      <div className="prose prose-sm text-gray-700"
                        dangerouslySetInnerHTML={{ __html: getInvestmentPhilosophy(clientRiskProfile.risk_profile_determined) }}
                      />
                    </div>

                    {/* Risk Metrics */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="bg-white rounded-lg border border-gray-200 p-4 text-center">
                        <div className="text-2xl font-bold text-ugmonk-dark">
                          {clientRiskProfile.risk_profile_determined === 'Conservative' ? '65%' :
                           clientRiskProfile.risk_profile_determined === 'Balanced' ? '85%' :
                           clientRiskProfile.risk_profile_determined === 'Aggressive' ? '95%' : '75%'}
                        </div>
                        <div className="text-sm text-gray-600">Success Probability</div>
                      </div>
                      <div className="bg-white rounded-lg border border-gray-200 p-4 text-center">
                        <div className="text-2xl font-bold text-ugmonk-dark">
                          {clientRiskProfile.risk_profile_determined === 'Conservative' ? '8%' :
                           clientRiskProfile.risk_profile_determined === 'Balanced' ? '12%' :
                           clientRiskProfile.risk_profile_determined === 'Aggressive' ? '18%' : '10%'}
                        </div>
                        <div className="text-sm text-gray-600">Maximum Drawdown</div>
                      </div>
                      <div className="bg-white rounded-lg border border-gray-200 p-4 text-center">
                        <div className="text-2xl font-bold text-green-600">
                          {clientRiskProfile.risk_profile_determined === 'Conservative' ? '0.45' :
                           clientRiskProfile.risk_profile_determined === 'Balanced' ? '0.65' :
                           clientRiskProfile.risk_profile_determined === 'Aggressive' ? '0.75' : '0.55'}
                        </div>
                        <div className="text-sm text-gray-600">Sharpe Ratio</div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-3 pt-4 border-t">
                      <Button className="bg-ugmonk-yellow hover:bg-[#f5cc6a] text-black">
                        Generate Model Portfolio Report
                      </Button>
                      <Button variant="outline" className="border-gray-300 text-gray-700 hover:bg-gray-50">
                        Download Factsheet
                      </Button>
                    </div>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="no-go-list">
                <NoGoList 
                  clientId={selectedClientId} 
                  clientName={clients.find(c => c.id === selectedClientId)?.full_name || prospects.find(p => p.id === selectedClientId)?.full_name || 'Client'}
                />
              </TabsContent>
            </Tabs>
          </DialogContent>
        </Dialog>

        {/* Override Risk Profile Dialog */}
        <Dialog open={overrideDialogOpen} onOpenChange={setOverrideDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="text-gray-900">Override Risk Profile</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <p className="text-sm text-gray-600">
                Use your professional discretion to assign a risk profile without the questionnaire.
              </p>
              
              {/* Risk Profile Selection */}
              <div className="space-y-2">
                <Label className="text-gray-700">Select Risk Profile</Label>
                <div className="grid grid-cols-1 gap-2">
                  {['Conservative', 'Balanced', 'Aggressive'].map((profile) => (
                    <button
                      key={profile}
                      onClick={() => setSelectedRiskProfile(profile)}
                      className={`p-4 rounded-lg border-2 text-left transition-all ${
                        selectedRiskProfile === profile
                          ? 'border-[#ffd87a] bg-[#ffd87a]/5'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="font-semibold text-gray-900">{profile}</div>
                      <div className="text-sm text-gray-600">
                        {profile === 'Conservative' && 'Low risk tolerance, capital preservation'}
                        {profile === 'Balanced' && 'Moderate risk, balanced growth'}
                        {profile === 'Aggressive' && 'High risk tolerance, growth focused'}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Reason for Override */}
              <div className="space-y-2">
                <Label htmlFor="override-reason" className="text-gray-700">
                  Reason for Override <span className="text-red-500">*</span>
                </Label>
                <textarea
                  id="override-reason"
                  value={overrideReason}
                  onChange={(e) => setOverrideReason(e.target.value)}
                  placeholder="Explain why you're overriding the questionnaire (e.g., client's investment experience, financial goals, etc.)"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ffd87a] focus:border-transparent min-h-[100px] resize-none"
                  required
                />
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4 border-t">
                <Button 
                  onClick={handleOverrideRiskProfile}
                  disabled={!selectedRiskProfile || !overrideReason.trim()}
                  className="flex-1 bg-ugmonk-yellow hover:bg-[#f5cc6a] text-black disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Confirm Override
                </Button>
                <Button 
                  onClick={() => {
                    setOverrideDialogOpen(false);
                    setSelectedRiskProfile('');
                    setOverrideReason('');
                  }}
                  variant="outline"
                  className="flex-1 border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Social Profile Dialog */}
        <Dialog open={socialProfileDialogOpen} onOpenChange={setSocialProfileDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-gray-900 text-2xl">Social Profile</DialogTitle>
              <p className="text-sm text-gray-600 mt-2">
                Behavioral and social insights based on education, lifestyle, travel, sports, and philanthropy
              </p>
            </DialogHeader>
            <div className="space-y-6 py-4">
              
              {/* Education */}
              <div className="space-y-2">
                <Label className="text-gray-700 font-semibold flex items-center gap-2">
                  <span className="text-ugmonk-dark">🎓</span>
                  Education & Professional Background
                </Label>
                <textarea
                  value={socialProfileForm.education}
                  onChange={(e) => setSocialProfileForm({...socialProfileForm, education: e.target.value})}
                  placeholder="e.g., MBA from IIM-A, Engineering from IIT-B, Professional certifications..."
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ffd87a] focus:border-transparent min-h-[80px] resize-none"
                />
              </div>

              {/* Travel Preferences */}
              <div className="space-y-2">
                <Label className="text-gray-700 font-semibold flex items-center gap-2">
                  <span className="text-ugmonk-dark">✈️</span>
                  Travel Preferences & Experiences
                </Label>
                <textarea
                  value={socialProfileForm.travel_preferences}
                  onChange={(e) => setSocialProfileForm({...socialProfileForm, travel_preferences: e.target.value})}
                  placeholder="e.g., Frequent international traveler, Adventure tourism, Luxury travel, Annual family trips to Europe..."
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ffd87a] focus:border-transparent min-h-[80px] resize-none"
                />
              </div>

              {/* Sports & Fitness */}
              <div className="space-y-2">
                <Label className="text-gray-700 font-semibold flex items-center gap-2">
                  <span className="text-ugmonk-dark">⚽</span>
                  Sports & Fitness Interests
                </Label>
                <textarea
                  value={socialProfileForm.sports_interests}
                  onChange={(e) => setSocialProfileForm({...socialProfileForm, sports_interests: e.target.value})}
                  placeholder="e.g., Golf club member, Marathon runner, Cricket enthusiast, Gym regular..."
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ffd87a] focus:border-transparent min-h-[80px] resize-none"
                />
              </div>

              {/* Philanthropy */}
              <div className="space-y-2">
                <Label className="text-gray-700 font-semibold flex items-center gap-2">
                  <span className="text-ugmonk-dark">❤️</span>
                  Philanthropy & Social Causes
                </Label>
                <textarea
                  value={socialProfileForm.philanthropy}
                  onChange={(e) => setSocialProfileForm({...socialProfileForm, philanthropy: e.target.value})}
                  placeholder="e.g., Supports education NGOs, Environmental activist, Regular charity donor, Board member of trust..."
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ffd87a] focus:border-transparent min-h-[80px] resize-none"
                />
              </div>

              {/* Lifestyle Indicators */}
              <div className="space-y-2">
                <Label className="text-gray-700 font-semibold flex items-center gap-2">
                  <span className="text-ugmonk-dark">🏡</span>
                  Lifestyle & Consumption Patterns
                </Label>
                <textarea
                  value={socialProfileForm.lifestyle_indicators}
                  onChange={(e) => setSocialProfileForm({...socialProfileForm, lifestyle_indicators: e.target.value})}
                  placeholder="e.g., Luxury car owner, Premium residential area, Art collector, Fine dining enthusiast..."
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ffd87a] focus:border-transparent min-h-[80px] resize-none"
                />
              </div>

              {/* Hobbies & Interests */}
              <div className="space-y-2">
                <Label className="text-gray-700 font-semibold flex items-center gap-2">
                  <span className="text-ugmonk-dark">🎨</span>
                  Hobbies & Personal Interests
                </Label>
                <textarea
                  value={socialProfileForm.hobbies}
                  onChange={(e) => setSocialProfileForm({...socialProfileForm, hobbies: e.target.value})}
                  placeholder="e.g., Photography, Reading, Music, Cooking, Gardening..."
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ffd87a] focus:border-transparent min-h-[80px] resize-none"
                />
              </div>

              {/* Professional Network */}
              <div className="space-y-2">
                <Label className="text-gray-700 font-semibold flex items-center gap-2">
                  <span className="text-ugmonk-dark">🤝</span>
                  Professional Network & Associations
                </Label>
                <textarea
                  value={socialProfileForm.professional_network}
                  onChange={(e) => setSocialProfileForm({...socialProfileForm, professional_network: e.target.value})}
                  placeholder="e.g., CII member, Rotary Club, Industry associations, Alumni networks..."
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ffd87a] focus:border-transparent min-h-[80px] resize-none"
                />
              </div>

              {/* Social Media Activity */}
              <div className="space-y-2">
                <Label className="text-gray-700 font-semibold flex items-center gap-2">
                  <span className="text-ugmonk-dark">📱</span>
                  Social Media Activity & Digital Footprint
                </Label>
                <textarea
                  value={socialProfileForm.social_media_activity}
                  onChange={(e) => setSocialProfileForm({...socialProfileForm, social_media_activity: e.target.value})}
                  placeholder="e.g., Active LinkedIn profile (10k+ connections), Twitter influencer, Instagram lifestyle content..."
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ffd87a] focus:border-transparent min-h-[80px] resize-none"
                />
              </div>

              {socialProfileData && (
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <p className="text-xs text-gray-600">
                    Last updated: {new Date(socialProfileData.last_updated).toLocaleString()}
                    {socialProfileData.updated_by_rm_name && ` by ${socialProfileData.updated_by_rm_name}`}
                  </p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4 border-t">
                <Button 
                  onClick={saveSocialProfile}
                  className="flex-1 bg-ugmonk-yellow hover:bg-[#f5cc6a] text-black"
                >
                  Save Social Profile
                </Button>
                <Button 
                  onClick={() => setSocialProfileDialogOpen(false)}
                  variant="outline"
                  className="flex-1 border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Portfolio Rebalancing Dialog */}
        <Dialog open={rebalanceDialogOpen} onOpenChange={setRebalanceDialogOpen}>
          <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-6xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-gray-900 text-2xl font-bold flex items-center gap-2">
                <RefreshCw className="w-6 h-6 text-purple-600" />
                Portfolio Rebalancing Report
              </DialogTitle>
            </DialogHeader>
            {rebalanceLoading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Analyzing portfolio and generating recommendations...</p>
              </div>
            ) : rebalanceData ? (
              <div className="space-y-6">
                {/* Client Summary */}
                <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-6 border border-purple-200">
                  <div className="grid grid-cols-4 gap-4">
                    <div>
                      <p className="text-sm text-purple-700 font-medium">Client Name</p>
                      <p className="text-lg font-bold text-gray-900">{rebalanceData.client_name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-purple-700 font-medium">Risk Profile</p>
                      <p className="text-lg font-bold text-gray-900">{rebalanceData.risk_profile}</p>
                    </div>
                    <div>
                      <p className="text-sm text-purple-700 font-medium">Portfolio Value</p>
                      <p className="text-lg font-bold text-gray-900">
                        {formatCurrency(rebalanceData.portfolio_value, user?.market)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-purple-700 font-medium">Total Actions</p>
                      <p className="text-lg font-bold text-gray-900">
                        {rebalanceData.rebalancing_summary.total_actions}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Model Portfolio */}
                <div className="bg-white rounded-xl p-6 border border-gray-200">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Target Model Portfolio</h3>
                  <p className="text-purple-600 font-semibold text-xl mb-4">{rebalanceData.model_portfolio}</p>
                  <div className="grid grid-cols-5 gap-4">
                    {Object.entries(rebalanceData.target_allocation).map(([asset, pct]) => (
                      <div key={asset} className="text-center p-3 bg-purple-50 rounded-lg">
                        <p className="text-sm text-gray-600">{asset}</p>
                        <p className="text-2xl font-bold text-purple-600">{pct}%</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Rebalancing Actions */}
                <div className="bg-white rounded-xl p-6 border border-gray-200">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Recommended Actions</h3>
                  <div className="space-y-3">
                    {rebalanceData.recommendations.map((rec, idx) => (
                      <div 
                        key={idx}
                        className={`p-4 rounded-lg border-l-4 ${
                          rec.priority === 'High' ? 'border-red-500 bg-red-50' :
                          rec.priority === 'Medium' ? 'border-yellow-500 bg-yellow-50' :
                          'border-green-500 bg-green-50'
                        }`}
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <h4 className="font-bold text-gray-900 text-lg">{rec.asset_class}</h4>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                rec.priority === 'High' ? 'bg-red-200 text-red-800' :
                                rec.priority === 'Medium' ? 'bg-yellow-200 text-yellow-800' :
                                'bg-green-200 text-green-800'
                              }`}>
                                {rec.priority} Priority
                              </span>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                rec.action === 'Buy' ? 'bg-green-200 text-green-800' : 'bg-red-200 text-red-800'
                              }`}>
                                {rec.action}
                              </span>
                            </div>
                            <div className="grid grid-cols-4 gap-4 text-sm">
                              <div>
                                <p className="text-gray-600">Current</p>
                                <p className="font-semibold text-gray-900">{rec.current_allocation}%</p>
                                <p className="text-xs text-gray-500">{formatCurrency(rec.current_value, user?.market)}</p>
                              </div>
                              <div>
                                <p className="text-gray-600">Target</p>
                                <p className="font-semibold text-gray-900">{rec.target_allocation}%</p>
                                <p className="text-xs text-gray-500">{formatCurrency(rec.target_value, user?.market)}</p>
                              </div>
                              <div>
                                <p className="text-gray-600">Difference</p>
                                <p className={`font-semibold ${rec.difference > 0 ? 'text-green-600' : 'text-red-600'}`}>
                                  {rec.difference > 0 ? '+' : ''}{rec.difference}%
                                </p>
                              </div>
                              <div>
                                <p className="text-gray-600">Action Amount</p>
                                <p className="font-semibold text-purple-600">
                                  {formatCurrency(rec.action_amount, user?.market)}
                                </p>
                              </div>
                            </div>
                          </div>
                          <ArrowUpDown className={`w-6 h-6 ${rec.action === 'Buy' ? 'text-green-600' : 'text-red-600'}`} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Suggested Holdings */}
                <div className="bg-white rounded-xl p-6 border border-gray-200">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Suggested Holdings (Based on Model Portfolio)</h3>
                  <div className="grid grid-cols-2 gap-4">
                    {rebalanceData.suggested_holdings.map((holding, idx) => (
                      <div key={idx} className="p-4 border border-gray-200 rounded-lg hover:border-purple-300 transition-colors">
                        <div className="flex justify-between items-start mb-2">
                          <h4 className="font-semibold text-gray-900 text-sm">{holding.security_name}</h4>
                          <span className="text-sm font-bold text-purple-600">{holding.allocation_percentage}%</span>
                        </div>
                        <p className="text-xs text-gray-600 mb-1">
                          <span className="font-medium">Asset Class:</span> {holding.asset_class}
                        </p>
                        {holding.rationale && (
                          <p className="text-xs text-gray-500 italic">"{holding.rationale}"</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Summary Footer */}
                <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-6 border border-gray-200">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Generated on</p>
                      <p className="text-sm font-medium text-gray-900">
                        {new Date(rebalanceData.generated_at).toLocaleString()}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-600 mb-1">Estimated Implementation Time</p>
                      <p className="text-sm font-medium text-gray-900">
                        {rebalanceData.rebalancing_summary.estimated_time}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-3">
                  <Button
                    className="flex-1 bg-purple-600 hover:bg-purple-700 text-white"
                    onClick={() => {
                      // In real implementation, this would initiate the rebalancing process
                      toast.success('Rebalancing instructions sent for execution');
                      setRebalanceDialogOpen(false);
                    }}
                  >
                    Execute Rebalancing
                  </Button>
                  <Button
                    variant="outline"
                    className="flex-1 border-gray-300 text-gray-700 hover:bg-gray-50"
                    onClick={() => setRebalanceDialogOpen(false)}
                  >
                    Close
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <p>No rebalancing data available</p>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Prospect Details Dialog */}
        <Dialog open={showProspectDetailsModal} onOpenChange={setShowProspectDetailsModal}>
          <DialogContent className="bg-gradient-to-br from-[#fffdf0] to-white border-2 border-[#1a1a1a] text-gray-900 max-w-7xl max-h-[90vh] overflow-y-auto shadow-2xl">
            <DialogHeader>
              <DialogTitle className="text-gray-900 text-2xl font-bold flex items-center gap-2">
                <User className="w-6 h-6 text-ugmonk-dark" />
                Prospect Details
              </DialogTitle>
            </DialogHeader>
            {loadingProspectDetails ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1a1a1a] mx-auto mb-4"></div>
                <p className="text-gray-600">Loading prospect details...</p>
              </div>
            ) : selectedProspect ? (
              <div className="space-y-6">
                {/* Prospect Info */}
                <div className="bg-gradient-to-br from-[#fffdf0] to-[#fffdf0] rounded-xl p-6 border border-ugmonk-border">
                  <div className="grid grid-cols-4 gap-4">
                    <div>
                      <p className="text-sm text-ugmonk-dark font-medium">Prospect Name</p>
                      <p className="text-lg font-bold text-gray-900">{selectedProspect.full_name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-ugmonk-dark font-medium">Group Name</p>
                      <p className="text-lg font-bold text-gray-900">{selectedProspect.group_name || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-ugmonk-dark font-medium">Email</p>
                      <p className="text-lg font-bold text-gray-900">{selectedProspect.email}</p>
                    </div>
                    <div>
                      <p className="text-sm text-ugmonk-dark font-medium">Mobile</p>
                      <p className="text-lg font-bold text-gray-900">{selectedProspect.mobile || 'N/A'}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4 mt-4">
                    <div>
                      <p className="text-sm text-ugmonk-dark font-medium">PAN</p>
                      <p className="text-lg font-bold text-gray-900">{selectedProspect.pan || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-ugmonk-dark font-medium">Account Name</p>
                      <p className="text-lg font-bold text-gray-900">{selectedProspect.account_name || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-ugmonk-dark font-medium">Status</p>
                      <span className="inline-flex px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">
                        Prospect
                      </span>
                    </div>
                  </div>
                </div>

                {/* Portfolio Section */}
                {prospectPortfolio ? (
                  <div className="space-y-4">
                    <h3 className="text-xl font-bold text-gray-900 border-b pb-2">Portfolio Information</h3>
                    {console.log('Rendering portfolio with holdings count:', prospectPortfolio?.holdings?.length || 0)}
                    
                    {/* Portfolio Summary */}
                    <div className="grid grid-cols-3 gap-4">
                      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                        <p className="text-sm text-gray-600 mb-1">Total Value</p>
                        <p className="text-3xl font-bold text-ugmonk-dark">
                          {formatCurrency(prospectPortfolio?.total_value || 0, user?.market)}
                        </p>
                      </div>
                      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                        <p className="text-sm text-gray-600 mb-1">Total Gain</p>
                        <p className={`text-3xl font-bold ${(prospectPortfolio?.total_gain || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {(prospectPortfolio?.total_gain || 0) >= 0 ? '+' : ''}{formatCurrency(Math.abs(prospectPortfolio?.total_gain || 0), user?.market)}
                        </p>
                      </div>
                      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                        <p className="text-sm text-gray-600 mb-1">Return %</p>
                        <p className={`text-3xl font-bold ${(prospectPortfolio?.total_gain_percentage || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {(prospectPortfolio?.total_gain_percentage || 0) >= 0 ? '+' : ''}{(prospectPortfolio?.total_gain_percentage || 0).toFixed(2)}%
                        </p>
                      </div>
                    </div>

                    {/* Enhanced Holdings Management */}
                    <div className="bg-white rounded-xl p-6 border border-gray-200">
                      <div className="flex items-center justify-between mb-6">
                        <h4 className="text-lg font-bold text-gray-900">Holdings Management</h4>
                        <div className="flex gap-2">
                          <Button
                            onClick={() => setShowAddHoldingForm(!showAddHoldingForm)}
                            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 text-sm flex items-center gap-2"
                          >
                            <Plus className="w-4 h-4" />
                            Add Holding
                          </Button>
                          {(prospectPortfolio?.holdings || []).length > 0 && (
                            <Button
                              onClick={async () => {
                                if (window.confirm('Are you sure you want to clear all holdings? This action cannot be undone.')) {
                                  try {
                                    const token = localStorage.getItem('token');
                                    const config = { headers: { Authorization: `Bearer ${token}` } };
                                    await axios.delete(`${API}/portfolios/user/${selectedProspect.id}/holdings`, config);
                                    
                                    // Refresh portfolio data
                                    const portfolioRes = await axios.get(`${API}/portfolios/user/${selectedProspect.id}`, config);
                                    setProspectPortfolio(portfolioRes.data);
                                    toast.success('All holdings cleared successfully');
                                  } catch (error) {
                                    toast.error('Failed to clear holdings');
                                  }
                                }
                              }}
                              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 text-sm flex items-center gap-2"
                            >
                              <Trash2 className="w-4 h-4" />
                              Clear All
                            </Button>
                          )}
                        </div>
                      </div>

                      {/* Add Holding Form */}
                      {showAddHoldingForm && (
                        <div className="bg-[#fffdf0] rounded-lg p-4 mb-6 border border-ugmonk-border">
                          <h5 className="font-semibold text-ugmonk-darker mb-4">Add New Holding</h5>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                            <div>
                              <Label className="text-sm font-medium text-gray-700">Security Name</Label>
                              <Input
                                value={newHolding.security_name}
                                onChange={(e) => setNewHolding({...newHolding, security_name: e.target.value})}
                                placeholder="e.g., Reliance Industries"
                                className="mt-1"
                              />
                            </div>
                            <div>
                              <Label className="text-sm font-medium text-gray-700">Type</Label>
                              <select
                                value={newHolding.type}
                                onChange={(e) => setNewHolding({...newHolding, type: e.target.value})}
                                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-ugmonk-yellow"
                              >
                                <option value="Equity">Equity</option>
                                <option value="Mutual Fund">Mutual Fund</option>
                                <option value="Bond">Bond</option>
                                <option value="ETF">ETF</option>
                              </select>
                            </div>
                            <div>
                              <Label className="text-sm font-medium text-gray-700">Asset Class</Label>
                              <select
                                value={newHolding.asset_class}
                                onChange={(e) => setNewHolding({...newHolding, asset_class: e.target.value})}
                                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-ugmonk-yellow"
                              >
                                <option value="Equity">Equity</option>
                                <option value="Debt">Debt</option>
                                <option value="Hybrid">Hybrid</option>
                                <option value="Commodity">Commodity</option>
                              </select>
                            </div>
                            <div>
                              <Label className="text-sm font-medium text-gray-700">Quantity</Label>
                              <Input
                                type="number"
                                value={newHolding.quantity}
                                onChange={(e) => setNewHolding({...newHolding, quantity: e.target.value})}
                                placeholder="100"
                                className="mt-1"
                              />
                            </div>
                            <div>
                              <Label className="text-sm font-medium text-gray-700">Avg Cost ({getCurrencySymbol(user?.market)})</Label>
                              <Input
                                type="number"
                                step="0.01"
                                value={newHolding.average_cost}
                                onChange={(e) => setNewHolding({...newHolding, average_cost: e.target.value})}
                                placeholder="2500.00"
                                className="mt-1"
                              />
                            </div>
                            <div>
                              <Label className="text-sm font-medium text-gray-700">Current Price ({getCurrencySymbol(user?.market)})</Label>
                              <Input
                                type="number"
                                step="0.01"
                                value={newHolding.current_price}
                                onChange={(e) => setNewHolding({...newHolding, current_price: e.target.value})}
                                placeholder="2580.00"
                                className="mt-1"
                              />
                            </div>
                          </div>
                          <div className="flex justify-end gap-2 mt-4">
                            <Button
                              onClick={() => {
                                setShowAddHoldingForm(false);
                                setNewHolding({
                                  security_name: '',
                                  type: 'Equity',
                                  quantity: '',
                                  average_cost: '',
                                  current_price: '',
                                  asset_class: 'Equity'
                                });
                              }}
                              variant="outline"
                              className="px-4 py-2 text-sm"
                            >
                              <X className="w-4 h-4 mr-1" />
                              Cancel
                            </Button>
                            <Button
                              onClick={async () => {
                                try {
                                  const token = localStorage.getItem('token');
                                  const config = { headers: { Authorization: `Bearer ${token}` } };
                                  
                                  const holdingData = {
                                    ...newHolding,
                                    quantity: parseFloat(newHolding.quantity),
                                    average_cost: parseFloat(newHolding.average_cost),
                                    current_price: parseFloat(newHolding.current_price),
                                    current_value: parseFloat(newHolding.quantity) * parseFloat(newHolding.current_price),
                                    gain: (parseFloat(newHolding.current_price) - parseFloat(newHolding.average_cost)) * parseFloat(newHolding.quantity),
                                    gain_percentage: ((parseFloat(newHolding.current_price) - parseFloat(newHolding.average_cost)) / parseFloat(newHolding.average_cost)) * 100
                                  };
                                  
                                  await axios.post(`${API}/portfolios/user/${selectedProspect.id}/holding`, holdingData, config);
                                  
                                  // Refresh portfolio data
                                  const portfolioRes = await axios.get(`${API}/portfolios/user/${selectedProspect.id}`, config);
                                  setProspectPortfolio(portfolioRes.data);
                                  
                                  setShowAddHoldingForm(false);
                                  setNewHolding({
                                    security_name: '',
                                    type: 'Equity',
                                    quantity: '',
                                    average_cost: '',
                                    current_price: '',
                                    asset_class: 'Equity'
                                  });
                                  toast.success('Holding added successfully');
                                } catch (error) {
                                  toast.error('Failed to add holding');
                                }
                              }}
                              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 text-sm"
                            >
                              <Save className="w-4 h-4 mr-1" />
                              Add Holding
                            </Button>
                          </div>
                        </div>
                      )}

                      {/* Holdings Table */}
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-gray-50 border-b border-gray-200">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Security</th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Quantity</th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Avg Cost</th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Current Price</th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Current Value</th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Gain/Loss</th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">%</th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {(prospectPortfolio?.holdings || []).length === 0 ? (
                              <tr>
                                <td colSpan="8" className="px-4 py-8 text-center text-gray-500">
                                  <div className="flex flex-col items-center gap-2">
                                    <TrendingUp className="w-8 h-8 text-gray-400" />
                                    <p>No holdings yet. Add holdings manually or upload portfolio documents.</p>
                                  </div>
                                </td>
                              </tr>
                            ) : (prospectPortfolio?.holdings || []).map((holding, idx) => (
                              <tr key={idx} className="hover:bg-gray-50">
                                {editingHolding === idx ? (
                                  // Edit mode
                                  <>
                                    <td className="px-4 py-3">
                                      <Input
                                        value={holding.security_name}
                                        onChange={(e) => {
                                          const updatedHoldings = [...prospectPortfolio.holdings];
                                          updatedHoldings[idx].security_name = e.target.value;
                                          setProspectPortfolio({...prospectPortfolio, holdings: updatedHoldings});
                                        }}
                                        className="w-full"
                                      />
                                    </td>
                                    <td className="px-4 py-3">
                                      <Input
                                        type="number"
                                        value={holding.quantity}
                                        onChange={(e) => {
                                          const updatedHoldings = [...prospectPortfolio.holdings];
                                          updatedHoldings[idx].quantity = parseFloat(e.target.value);
                                          setProspectPortfolio({...prospectPortfolio, holdings: updatedHoldings});
                                        }}
                                        className="w-full"
                                      />
                                    </td>
                                    <td className="px-4 py-3">
                                      <Input
                                        type="number"
                                        step="0.01"
                                        value={holding.average_cost}
                                        onChange={(e) => {
                                          const updatedHoldings = [...prospectPortfolio.holdings];
                                          updatedHoldings[idx].average_cost = parseFloat(e.target.value);
                                          setProspectPortfolio({...prospectPortfolio, holdings: updatedHoldings});
                                        }}
                                        className="w-full"
                                      />
                                    </td>
                                    <td className="px-4 py-3">
                                      <Input
                                        type="number"
                                        step="0.01"
                                        value={holding.current_price}
                                        onChange={(e) => {
                                          const updatedHoldings = [...prospectPortfolio.holdings];
                                          updatedHoldings[idx].current_price = parseFloat(e.target.value);
                                          updatedHoldings[idx].current_value = updatedHoldings[idx].quantity * parseFloat(e.target.value);
                                          updatedHoldings[idx].gain = (parseFloat(e.target.value) - updatedHoldings[idx].average_cost) * updatedHoldings[idx].quantity;
                                          updatedHoldings[idx].gain_percentage = ((parseFloat(e.target.value) - updatedHoldings[idx].average_cost) / updatedHoldings[idx].average_cost) * 100;
                                          setProspectPortfolio({...prospectPortfolio, holdings: updatedHoldings});
                                        }}
                                        className="w-full"
                                      />
                                    </td>
                                    <td className="px-4 py-3 text-sm font-semibold text-gray-900">
                                      {formatCurrency(holding?.current_value || 0, user?.market)}
                                    </td>
                                    <td className="px-4 py-3">
                                      <span className={`text-sm font-semibold ${(holding?.gain || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                        {(holding?.gain || 0) >= 0 ? '+' : ''}{formatCurrency(Math.abs(holding?.gain || 0), user?.market)}
                                      </span>
                                    </td>
                                    <td className="px-4 py-3">
                                      <span className={`text-sm font-semibold ${(holding?.gain_percentage || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                        {(holding?.gain_percentage || 0) >= 0 ? '+' : ''}{(holding?.gain_percentage || 0).toFixed(2)}%
                                      </span>
                                    </td>
                                    <td className="px-4 py-3">
                                      <div className="flex gap-1">
                                        <Button
                                          onClick={async () => {
                                            try {
                                              const token = localStorage.getItem('token');
                                              const config = { headers: { Authorization: `Bearer ${token}` } };
                                              
                                              // Update the holding
                                              await axios.put(`${API}/portfolios/user/${selectedProspect.id}/holding/${idx}`, holding, config);
                                              
                                              // Exit edit mode immediately
                                              setEditingHolding(null);
                                              toast.success('Holding updated successfully');
                                              
                                              // Refresh portfolio data in background
                                              try {
                                                const portfolioRes = await axios.get(`${API}/portfolios/user/${selectedProspect.id}`, config);
                                                setProspectPortfolio(portfolioRes.data);
                                              } catch (refreshError) {
                                                console.warn('Portfolio refresh failed, but update was successful:', refreshError);
                                                // Don't show error to user since the update itself succeeded
                                              }
                                              
                                            } catch (error) {
                                              console.error('Update holding error:', error);
                                              
                                              // More specific error handling
                                              if (error.response?.status === 200) {
                                                // Success but axios treated as error
                                                setEditingHolding(null);
                                                toast.success('Holding updated successfully');
                                              } else if (error.response?.status === 404) {
                                                toast.error('Holding not found');
                                              } else if (error.response?.status === 403) {
                                                toast.error('Not authorized to update holding');
                                              } else {
                                                toast.error(`Update failed: ${error.response?.data?.detail || error.message}`);
                                              }
                                            }
                                          }}
                                          className="bg-green-600 hover:bg-green-700 text-white px-2 py-1 text-xs"
                                        >
                                          <Save className="w-3 h-3" />
                                        </Button>
                                        <Button
                                          onClick={() => setEditingHolding(null)}
                                          variant="outline"
                                          className="px-2 py-1 text-xs"
                                        >
                                          <X className="w-3 h-3" />
                                        </Button>
                                      </div>
                                    </td>
                                  </>
                                ) : (
                                  // View mode
                                  <>
                                    <td className="px-4 py-3">
                                      <div className="font-medium text-gray-900">{holding.security_name}</div>
                                      <div className="text-xs text-gray-500">{holding.type}</div>
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-900">{holding?.quantity || 0}</td>
                                    <td className="px-4 py-3 text-sm text-gray-900">{getCurrencySymbol(user?.market)}{(holding?.average_cost || 0).toFixed(2)}</td>
                                    <td className="px-4 py-3 text-sm text-gray-900">{getCurrencySymbol(user?.market)}{(holding?.current_price || 0).toFixed(2)}</td>
                                    <td className="px-4 py-3 text-sm font-semibold text-gray-900">
                                      {formatCurrency(holding?.current_value || 0, user?.market)}
                                    </td>
                                    <td className="px-4 py-3">
                                      <span className={`text-sm font-semibold ${(holding?.gain || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                        {(holding?.gain || 0) >= 0 ? '+' : ''}{formatCurrency(Math.abs(holding?.gain || 0), user?.market)}
                                      </span>
                                    </td>
                                    <td className="px-4 py-3">
                                      <span className={`text-sm font-semibold ${(holding?.gain_percentage || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                        {(holding?.gain_percentage || 0) >= 0 ? '+' : ''}{(holding?.gain_percentage || 0).toFixed(2)}%
                                      </span>
                                    </td>
                                    <td className="px-4 py-3">
                                      <div className="flex gap-1">
                                        <Button
                                          onClick={() => setEditingHolding(idx)}
                                          className="bg-ugmonk-yellow hover:bg-[#f5cc6a] text-black px-2 py-1 text-xs"
                                        >
                                          <Edit className="w-3 h-3" />
                                        </Button>
                                        <Button
                                          onClick={async () => {
                                            if (window.confirm('Are you sure you want to delete this holding?')) {
                                              try {
                                                const token = localStorage.getItem('token');
                                                const config = { headers: { Authorization: `Bearer ${token}` } };
                                                
                                                await axios.delete(`${API}/portfolios/user/${selectedProspect.id}/holding/${idx}`, config);
                                                
                                                // Refresh portfolio data
                                                const portfolioRes = await axios.get(`${API}/portfolios/user/${selectedProspect.id}`, config);
                                                setProspectPortfolio(portfolioRes.data);
                                                
                                                toast.success('Holding deleted successfully');
                                              } catch (error) {
                                                toast.error('Failed to delete holding');
                                              }
                                            }
                                          }}
                                          className="bg-red-600 hover:bg-red-700 text-white px-2 py-1 text-xs"
                                        >
                                          <Trash2 className="w-3 h-3" />
                                        </Button>
                                      </div>
                                    </td>
                                  </>
                                )}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Asset Allocation */}
                    {prospectPortfolio?.asset_allocation && Object.keys(prospectPortfolio.asset_allocation).length > 0 && (
                      <div className="bg-white rounded-xl p-6 border border-gray-200">
                        <h4 className="text-lg font-bold text-gray-900 mb-4">Asset Allocation</h4>
                        <div className="grid grid-cols-5 gap-4">
                          {Object.entries(prospectPortfolio.asset_allocation).map(([asset, percentage]) => (
                            <div key={asset} className="text-center p-4 bg-gray-50 rounded-lg">
                              <p className="text-sm text-gray-600 mb-1">{asset}</p>
                              <p className="text-2xl font-bold text-ugmonk-dark">{percentage}%</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <p>No portfolio data available for this prospect</p>
                  </div>
                )}

                {/* Documents Section */}
                <div className="space-y-4">
                  <h3 className="text-xl font-bold text-gray-900 border-b pb-2">Documents</h3>
                  
                  {/* Upload Document */}
                  <div className="bg-gradient-to-br from-white to-[#fffdf0] rounded-xl p-6 border-2 border-[#1a1a1a] shadow-lg">
                    <h4 className="text-lg font-bold text-ugmonk-dark mb-4 flex items-center gap-2">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                      Upload Document
                    </h4>
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="document-upload-input" className="text-sm text-gray-700 mb-2 block">
                          Select Image or PDF (Max 10MB)
                        </Label>
                        <Input
                          id="document-upload-input"
                          type="file"
                          accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,application/pdf"
                          onChange={handleFileSelect}
                          className="bg-white border-gray-300 focus:border-[#1a1a1a] focus:ring-[#1a1a1a]"
                        />
                        {selectedFile && (
                          <p className="text-sm text-gray-600 mt-2">
                            Selected: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)} KB)
                          </p>
                        )}
                      </div>
                      <div className="flex justify-end">
                        <Button
                          onClick={handleDocumentUpload}
                          disabled={!selectedFile || uploadingDocument}
                          className="bg-[#1a1a1a] hover:bg-[#1a1a1a] text-white px-6 py-2"
                        >
                          {uploadingDocument ? 'Uploading...' : 'Upload Document'}
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Uploaded Documents List */}
                  {prospectDocuments.length > 0 ? (
                    <div className="bg-white rounded-xl p-6 border border-gray-200">
                      <h4 className="text-lg font-bold text-gray-900 mb-4">Uploaded Documents ({prospectDocuments.length})</h4>
                      <div className="space-y-3">
                        {prospectDocuments.map((doc) => (
                          <div key={doc.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-[#1a1a1a] transition-colors">
                            <div className="flex items-center gap-3 flex-1">
                              <div className={`p-2 rounded-lg ${
                                doc.file_type.startsWith('image/') ? 'bg-[#fffdf0]' : 'bg-red-100'
                              }`}>
                                {doc.file_type.startsWith('image/') ? (
                                  <svg className="w-6 h-6 text-ugmonk-dark" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                  </svg>
                                ) : (
                                  <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                  </svg>
                                )}
                              </div>
                              <div className="flex-1">
                                <p className="font-semibold text-gray-900">{doc.filename}</p>
                                <p className="text-xs text-gray-500">
                                  Uploaded by {doc.uploaded_by_name} on {new Date(doc.uploaded_at).toLocaleDateString()}
                                </p>
                                <p className="text-xs text-gray-500">
                                  Size: {(doc.file_size / 1024).toFixed(2)} KB
                                </p>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={() => downloadDocument(doc.id)}
                                className="p-2 text-ugmonk-dark hover:bg-[#fffdf0] rounded-lg transition-colors"
                                title="Download"
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                </svg>
                              </button>
                              <button
                                onClick={() => deleteDocument(doc.id)}
                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                title="Delete"
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="bg-gray-50 rounded-xl p-6 border border-gray-200 text-center">
                      <p className="text-gray-500">No documents uploaded yet</p>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex gap-3">
                  <Button
                    className="flex-1 bg-[#1a1a1a] hover:bg-[#1a1a1a] text-white"
                    onClick={() => convertToClient(selectedProspect.id)}
                  >
                    Convert to Client
                  </Button>
                  <Button
                    variant="outline"
                    className="flex-1 border-gray-300 text-gray-700 hover:bg-gray-50"
                    onClick={() => setShowProspectDetailsModal(false)}
                  >
                    Close
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <p>No prospect details available</p>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Portfolio Thresholds Dialog */}
        {thresholdsDialogOpen && (
          <Dialog open={thresholdsDialogOpen} onOpenChange={setThresholdsDialogOpen}>
            <DialogContent className="bg-white max-w-6xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-gray-900 text-2xl">Portfolio Thresholds</DialogTitle>
              </DialogHeader>
              <PortfolioThresholds
                clientId={thresholdsClientId}
                clientName={thresholdsClientName}
                onClose={() => setThresholdsDialogOpen(false)}
              />
            </DialogContent>
          </Dialog>
        )}
      </div>
    </Layout>
  );
};

export default ClientsProspects;
